"use client"

import { useState } from "react"
import Link from "next/link"
import { Eye, ExternalLink } from "lucide-react"
import type { EntityCard } from "@/lib/ai-chat/types"
import { EntityPreviewModal } from "./entity-preview-modal"

/**
 * The result card for something the assistant just created or changed.
 *
 * Replaces the one-line status chip ("Creating phishing scenario ✓") for the
 * kinds the server describes, so the operator can see WHAT was made, open it,
 * and — where a rendered preview exists — look at it without leaving the
 * conversation.
 *
 * The card owns identity only: name, kind, a few facts, and the two ways out.
 * The preview itself lives in a modal, because the chat companion is a
 * picture-in-picture panel or a narrow drawer and an email rendered inside it
 * is too small to review. Nothing is fetched until the modal is opened.
 */

const KIND_LABEL: Record<EntityCard["kind"], string> = {
  scenario: "Scenario",
  campaign: "Campaign",
  announcement: "Announcement",
  deck: "Deck",
}

export function EntityResultCard({ entity }: { entity: EntityCard }) {
  const [previewOpen, setPreviewOpen] = useState(false)

  return (
    <div className="my-2 rounded-lg border border-border bg-muted/30 overflow-hidden text-sm">
      <div className="px-3 py-2 grid gap-1">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {KIND_LABEL[entity.kind]}
        </div>

        <Link
          href={entity.href}
          className="font-medium text-primary hover:underline break-words inline-flex items-center gap-1 w-fit"
        >
          {entity.name}
          <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
        </Link>

        {entity.facts.length > 0 && (
          <dl className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
            {entity.facts.map((f) => (
              <div key={f.label} className="flex gap-1 min-w-0">
                <dt className="shrink-0">{f.label}:</dt>
                <dd className="text-foreground/80 truncate">{f.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {entity.previewPath && (
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          className="w-full px-3 py-1.5 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border-t border-border/60"
        >
          <Eye className="h-3 w-3" aria-hidden="true" />
          Preview
        </button>
      )}

      {/* Mounted only once opened, so a message carrying several cards does not
          stack several dialogs' worth of state before anyone asks for one. */}
      {previewOpen && (
        <EntityPreviewModal
          entity={entity}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
        />
      )}
    </div>
  )
}
